import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const GAP_ABOVE_FOOTER = 20;
const GAP_ABOVE_PAGINATOR = 16;
const MIN_BOTTOM = 20;
const SCROLL_END_THRESHOLD = 4;
const MIN_SCROLLABLE_OVERFLOW_PX = 16;
/** Igual ao Angular: `.cotin-paginator-slot` (wrapper com padding) evita o FAB cobrir as setas. */
const PAGINATOR_SELECTORS = '.cotin-paginator-slot, .cotin-pagination-wrapper, .pagination-container';

function getMaxWindowScrollY(): number {
    const html = document.documentElement;
    const body = document.body;
    const scrollHeight = Math.max(body?.scrollHeight ?? 0, html.scrollHeight);
    return Math.max(0, scrollHeight - window.innerHeight);
}

function computeBottomLiftForPaginatorBars(vh: number): number {
    let lift = MIN_BOTTOM;
    /** Faixa inferior da tela (px) onde o FAB costuma conflitar com paginação — igual Angular */
    const bottomStripPx = 120;
    const nodes = document.querySelectorAll<HTMLElement>(PAGINATOR_SELECTORS);

    for (let i = 0; i < nodes.length; i++) {
        const rect = nodes[i].getBoundingClientRect();
        if (rect.width < 8 || rect.height < 8) {
            continue;
        }
        if (rect.bottom <= 0 || rect.top >= vh) {
            continue;
        }

        const stripTop = vh - bottomStripPx;
        if (rect.bottom <= stripTop || rect.top >= vh) {
            continue;
        }

        const need = Math.ceil(vh - rect.top + GAP_ABOVE_PAGINATOR);
        if (need > lift) {
            lift = need;
        }
    }

    return lift;
}

const ScrollButtons: React.FC = () => {
    const location = useLocation();
    const [isTop, setIsTop] = useState(true);
    const [isBottom, setIsBottom] = useState(false);
    const [pageIsScrollable, setPageIsScrollable] = useState(false);
    const [fabBottomPx, setFabBottomPx] = useState(24);
    const layoutObserverRef = useRef<ResizeObserver | null>(null);

    const updateFabBottom = useCallback(() => {
        const vh = window.innerHeight;
        const maxLift = Math.min(vh * 0.5, vh - 96);
        const liftFromPaginator = computeBottomLiftForPaginatorBars(vh);

        const footer =
            document.querySelector<HTMLElement>('footer') ??
            document.querySelector<HTMLElement>('app-footer') ??
            document.querySelector<HTMLElement>('.footer');

        let liftFromFooter = MIN_BOTTOM;
        if (footer) {
            const fr = footer.getBoundingClientRect();
            if (fr.top < vh) {
                liftFromFooter = Math.round(vh - fr.top + GAP_ABOVE_FOOTER);
            }
        }

        const combined = Math.max(MIN_BOTTOM, liftFromFooter, liftFromPaginator);
        setFabBottomPx(Math.min(Math.round(combined), Math.round(maxLift)));
    }, []);

    const checkScrollPosition = useCallback(() => {
        const pageIsScrollableNow = getMaxWindowScrollY() >= MIN_SCROLLABLE_OVERFLOW_PX;
        setPageIsScrollable(pageIsScrollableNow);

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = Math.max(
            document.body?.scrollHeight ?? 0,
            document.documentElement.scrollHeight
        );

        setIsTop(scrollTop <= SCROLL_END_THRESHOLD);
        setIsBottom(scrollTop + windowHeight >= documentHeight - SCROLL_END_THRESHOLD);
        updateFabBottom();
    }, [updateFabBottom]);

    useEffect(() => {
        const runAfterLayout = () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    checkScrollPosition();
                });
            });
        };

        checkScrollPosition();
        queueMicrotask(() => updateFabBottom());
        runAfterLayout();

        const onWindowScroll = () => checkScrollPosition();
        const onWindowResize = () => checkScrollPosition();

        window.addEventListener('scroll', onWindowScroll, { passive: true });
        window.addEventListener('resize', onWindowResize);

        if (typeof ResizeObserver !== 'undefined') {
            layoutObserverRef.current = new ResizeObserver(() => {
                checkScrollPosition();
                updateFabBottom();
            });
            layoutObserverRef.current.observe(document.documentElement);
            if (document.body) {
                layoutObserverRef.current.observe(document.body);
            }
            document.querySelectorAll<HTMLElement>(PAGINATOR_SELECTORS).forEach((el) => {
                layoutObserverRef.current?.observe(el);
            });
        }

        return () => {
            window.removeEventListener('scroll', onWindowScroll);
            window.removeEventListener('resize', onWindowResize);
            layoutObserverRef.current?.disconnect();
            layoutObserverRef.current = null;
        };
    }, [checkScrollPosition, updateFabBottom, location.pathname, location.search]);

    const rolarParaCima = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const rolarParaBaixo = () => {
        const maxScroll = Math.max(
            document.body?.scrollHeight ?? 0,
            document.documentElement.scrollHeight
        );
        window.scrollTo({ top: maxScroll, behavior: 'smooth' });
    };

    if (!pageIsScrollable || (isTop && isBottom)) {
        return null;
    }

    return (
        <div className="scroll-buttons" style={{ bottom: fabBottomPx }}>
            {!isTop && (
                <button
                    type="button"
                    className="btn-scroll"
                    onClick={rolarParaCima}
                    title="Rolar para o topo"
                    aria-label="Rolar para o topo"
                >
                    <i className="fas fa-chevron-up" aria-hidden="true" />
                </button>
            )}
            {!isBottom && (
                <button
                    type="button"
                    className="btn-scroll"
                    onClick={rolarParaBaixo}
                    title="Rolar para o fundo"
                    aria-label="Rolar para o fundo"
                >
                    <i className="fas fa-chevron-down" aria-hidden="true" />
                </button>
            )}
        </div>
    );
};

export default ScrollButtons;
